import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import {
  AlertCircle,
  ArrowRight,
  Check,
  Loader2,
  Lock,
  Sparkles,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Eyebrow, Rule } from "@/components/editorial";
import { useRevenueCat } from "@/hooks/useRevenueCat";
import type { BillingPeriod, NormalizedPackage } from "@/lib/purchases";
import { useEntitlements } from "./useEntitlements";

/** Only features that are actually premium-gated server-side (see the
 *  gating matrix in docs/monetisation-referral-ambassador.md). */
const LOSS_KEYS = [
  "unlimited",
  "swap",
  "filters",
  "assistant",
  "photo",
] as const;

type PlanId = "monthly" | "annual";

const PLAN_PACKAGE_IDS: Record<PlanId, string> = {
  monthly: "$rc_monthly",
  annual: "$rc_annual",
};

const PLAN_ORDER: PlanId[] = ["monthly", "annual"];

interface PlanView {
  id: PlanId;
  label: string;
  price: string;
  suffix: string;
  note: string | null;
  badge: string | null;
}

function formatMoney(amount: number, currency: string): string | null {
  try {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency,
    }).format(amount);
  } catch {
    return null;
  }
}

function PlanCard({
  plan,
  selected,
  onSelect,
}: {
  plan: PlanView;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={[
        "relative flex w-full flex-col gap-2 rounded-lg p-4 text-left transition-all duration-150",
        selected ? "border-2 border-foreground" : "border border-hair",
      ].join(" ")}
    >
      {/* Radio dot */}
      <span
        className={[
          "flex size-4 shrink-0 items-center justify-center rounded-full border-2 transition-all",
          selected ? "border-foreground" : "border-ink-40",
        ].join(" ")}
      >
        {selected && <span className="size-2 rounded-full bg-foreground" />}
      </span>

      {/* Savings badge */}
      {plan.badge && (
        <span className="absolute right-3 top-3 rounded-full bg-foreground px-2 py-0.5 text-[10px] font-semibold tracking-wide text-background">
          {plan.badge}
        </span>
      )}

      {/* Label */}
      <p className="text-[11px] font-semibold uppercase tracking-widest text-ink-50">
        {plan.label}
      </p>

      {/* Price */}
      <div className="flex items-baseline gap-0.5">
        <span className="font-serif text-[26px] font-normal leading-none tracking-tight">
          {plan.price}
        </span>
        {plan.suffix && (
          <span className="text-[12px] text-ink-50">{plan.suffix}</span>
        )}
      </div>

      {/* Annual note */}
      {plan.note && <p className="text-[11px] text-ink-50">{plan.note}</p>}
    </button>
  );
}

/**
 * Slide-up paywall with plan selector (monthly / annual).
 *
 * Prices, currency and free-trial promises are read from the store package
 * returned by RevenueCat — never hard-coded — so what is shown is what the
 * store will charge (App Store guideline 3.1.2, consumer law).
 *
 * Variant "read_only": user's trial has expired — messaging focuses on
 * resuming access rather than discovering features.
 *
 * Works on both native (Capacitor SDK) and web (RevenueCat Web Billing).
 */
export function PaywallSheet({
  open,
  onOpenChange,
  feature,
  variant = "default",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  feature?: string;
  variant?: "default" | "read_only";
}) {
  const { t } = useTranslation();
  const { state, packages, purchase, restore, resetPurchaseState } =
    useRevenueCat();
  const { refetch } = useEntitlements();
  // Default to annual — conversion research consistently shows annual-first outperforms monthly-first.
  const [selectedPlan, setSelectedPlan] = useState<PlanId>("annual");

  // A previous purchase/restore outcome (error, cancelled, done) must not
  // greet the user the next time the sheet opens.
  useEffect(() => {
    if (open) resetPurchaseState();
  }, [open, resetPurchaseState]);

  const unlocked =
    state.status === "purchased" ||
    (state.status === "restored" && state.hasEntitlement);

  useEffect(() => {
    if (!unlocked) return;
    void refetch();
    const timer = setTimeout(() => {
      onOpenChange(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, [unlocked, refetch, onOpenChange]);

  const pkgFor = (id: PlanId): NormalizedPackage | undefined =>
    packages.find((p) => p.identifier === PLAN_PACKAGE_IDS[id]);
  const monthlyPkg = pkgFor("monthly");
  const annualPkg = pkgFor("annual");
  const selectedPkg = pkgFor(selectedPlan);

  const trialLabel = (trial: BillingPeriod) =>
    t(`paywall.free_trial_${trial.unit}`, { count: trial.count });

  const planViews: PlanView[] = PLAN_ORDER.map((id) => {
    const pkg = id === "monthly" ? monthlyPkg : annualPkg;
    const label = t(
      id === "monthly" ? "paywall.plan_monthly" : "paywall.plan_annual",
    );
    if (!pkg?.priceString) {
      return {
        id,
        label,
        price: t("paywall.price_unavailable"),
        suffix: "",
        note: null,
        badge: null,
      };
    }
    if (id === "monthly") {
      return {
        id,
        label,
        price: pkg.priceString,
        suffix: t("paywall.per_month"),
        note: pkg.freeTrial ? trialLabel(pkg.freeTrial) : null,
        badge: null,
      };
    }
    // Annual: show the monthly equivalent when the amount is known.
    const perMonth =
      pkg.price !== null && pkg.currencyCode
        ? formatMoney(pkg.price / 12, pkg.currencyCode)
        : null;
    let badge: string | null = null;
    if (
      pkg.price !== null &&
      monthlyPkg?.price &&
      monthlyPkg.currencyCode === pkg.currencyCode
    ) {
      const percent = Math.round(
        (1 - pkg.price / (monthlyPkg.price * 12)) * 100,
      );
      if (percent > 0) badge = t("paywall.savings_badge", { percent });
    }
    const billed = t("paywall.billed_yearly", { price: pkg.priceString });
    return {
      id,
      label,
      price: perMonth ?? pkg.priceString,
      suffix: t(perMonth ? "paywall.per_month" : "paywall.per_year"),
      note: pkg.freeTrial
        ? `${perMonth ? `${billed} · ` : ""}${trialLabel(pkg.freeTrial)}`
        : perMonth
          ? billed
          : null,
      badge,
    };
  });

  const handlePurchase = async () => {
    if (selectedPkg) await purchase(selectedPkg);
  };

  const isLoading =
    state.status === "loading" ||
    state.status === "purchasing" ||
    state.status === "restoring";
  const isUnconfigured = state.status === "unconfigured";
  const ctaDisabled = isLoading || unlocked || isUnconfigured || !selectedPkg;
  const isReadOnly = variant === "read_only";
  const canRestore =
    state.status === "ready" ||
    state.status === "restored" ||
    (state.status === "error" && state.source !== "configure");

  const ctaLabel = isReadOnly
    ? t("paywall.resume_cta")
    : selectedPkg?.freeTrial
      ? t("paywall.cta_trial", { trial: trialLabel(selectedPkg.freeTrial) })
      : selectedPlan === "monthly"
        ? t("paywall.cta_monthly")
        : t("paywall.cta_annual");

  const errorMessage =
    state.status === "error"
      ? t(
          state.source === "configure"
            ? "paywall.configure_error"
            : state.source === "restore"
              ? "paywall.restore_error"
              : "paywall.purchase_error",
        )
      : null;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-foreground/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in" />
        <Dialog.Content
          // The default variant without a feature has no Description element;
          // tell Radix so instead of leaving a dangling aria-describedby.
          {...(!isReadOnly && !feature
            ? { "aria-describedby": undefined }
            : {})}
          className="fixed inset-x-0 bottom-0 z-50 mx-auto max-w-md border-t border-hair bg-background pb-[env(safe-area-inset-bottom)] shadow-soft-warm-lg data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom-4 sm:bottom-auto sm:top-1/2 sm:max-w-lg sm:-translate-y-1/2 sm:border sm:rounded-none"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-hair px-6 py-4">
            <Eyebrow accent>
              <Sparkles className="size-3" /> {t("paywall.eyebrow")}
            </Eyebrow>
            <Dialog.Close
              aria-label={t("paywall.close")}
              className="text-foreground/60 hover:text-foreground"
            >
              <X className="size-4" />
            </Dialog.Close>
          </div>

          <div className="px-6 py-7">
            {/* Title */}
            {isReadOnly ? (
              <>
                <Dialog.Title className="font-serif text-[32px] font-normal leading-[1.1] tracking-[-0.02em]">
                  {t("paywall.readonly_title_before")}{" "}
                  <span className="italic">
                    {t("paywall.readonly_title_accent")}
                  </span>
                  .
                </Dialog.Title>
                <Dialog.Description className="mt-3 text-[15px] leading-relaxed text-ink-70">
                  {t("paywall.readonly_body")}
                </Dialog.Description>
              </>
            ) : (
              <>
                <Dialog.Title className="font-serif text-[36px] font-normal leading-[1] tracking-[-0.02em]">
                  {feature ? (
                    <>
                      {t("paywall.feature_title_before")}{" "}
                      <span className="italic">
                        {t("paywall.feature_title_accent")}
                      </span>{" "}
                      {t("paywall.feature_title_after")}
                    </>
                  ) : (
                    <>
                      {t("paywall.title_before")}{" "}
                      <span className="italic">
                        {t("paywall.title_accent")}
                      </span>
                      .
                    </>
                  )}
                </Dialog.Title>
                {feature && (
                  <Dialog.Description className="mt-3 font-serif text-[17px] font-light italic leading-snug text-ink-70">
                    {feature}
                  </Dialog.Description>
                )}
              </>
            )}

            {/* Plan selector */}
            <div className="mt-6 grid grid-cols-2 gap-3">
              {planViews.map((plan) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  selected={selectedPlan === plan.id}
                  onSelect={() => setSelectedPlan(plan.id)}
                />
              ))}
            </div>

            <Rule className="my-6" />

            {/* Loss-aversion framing */}
            <p className="mb-3 text-[11px] font-medium uppercase tracking-wider text-ink-50">
              {t("paywall.losses_heading")}
            </p>
            <ul className="space-y-3">
              {LOSS_KEYS.map((key) => (
                <li
                  key={key}
                  className="flex items-start gap-3 text-[14px] text-foreground"
                >
                  <Lock className="mt-0.5 size-3.5 shrink-0 text-ink-40" />
                  <span>{t(`paywall.features.${key}`)}</span>
                </li>
              ))}
            </ul>

            {/* CTA */}
            <Button
              size="xl"
              className="mt-8 w-full"
              onClick={handlePurchase}
              disabled={ctaDisabled}
            >
              {isLoading ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  {state.status === "purchasing"
                    ? t("paywall.cta_purchasing")
                    : state.status === "restoring"
                      ? t("paywall.restoring")
                      : t("paywall.cta_loading")}
                </>
              ) : unlocked ? (
                <>
                  <Check className="size-4" />{" "}
                  {state.status === "restored"
                    ? t("paywall.cta_restored")
                    : t("paywall.cta_purchased")}
                </>
              ) : state.status === "unconfigured" ? (
                state.reason === "signed-out" ? (
                  t("paywall.cta_sign_in")
                ) : (
                  t("paywall.cta_unavailable")
                )
              ) : (
                <>
                  {ctaLabel} <ArrowRight />
                </>
              )}
            </Button>

            {errorMessage && (
              <div
                role="alert"
                className="mt-3 flex items-start gap-2 rounded-pill border border-destructive/20 bg-destructive/5 px-3 py-2"
              >
                <AlertCircle className="mt-0.5 size-3.5 shrink-0 text-destructive" />
                <p className="text-[12px] text-destructive">{errorMessage}</p>
              </div>
            )}

            {state.status === "restored" && !state.hasEntitlement && (
              <p
                role="status"
                className="mt-3 text-center text-[12px] text-ink-70"
              >
                {t("paywall.restore_none")}
              </p>
            )}

            <p className="mt-3 text-center text-[12px] text-ink-50">
              {unlocked ? t("paywall.redirecting") : t("paywall.footer")}
            </p>

            {canRestore && !unlocked && (
              <button
                type="button"
                onClick={() => void restore()}
                className="mx-auto mt-2 block text-[12px] text-ink-50 underline underline-offset-2 hover:text-foreground"
              >
                {t("paywall.restore")}
              </button>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
